/* ==========================================================================
   Void Accordion - Loid Shopify Theme
   Accessible accordion with smooth height animation.
   Vanilla JS, no dependencies.
   ========================================================================== */

class VoidAccordion {
  #container;
  #singleMode;
  #items;
  #controller = new AbortController();

  /**
   * @param {HTMLElement} container - Element with .void-accordion
   */
  constructor(container) {
    this.#container = container;
    this.#singleMode = container.getAttribute('data-void-accordion-single') === 'true';
    this.#items = Array.from(container.querySelectorAll('.void-accordion__item'));

    this.#bindEvents();
    this.#initOpenItems();

    container.setAttribute('data-void-accordion-init', 'true');
  }

  /* -- Event binding ----------------------------------------------------- */

  #bindEvents() {
    const { signal } = this.#controller;

    for (const item of this.#items) {
      const trigger = item.querySelector('.void-accordion__trigger');
      if (!trigger) continue;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.#toggle(item);
      }, { signal });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.#toggle(item);
        }
      }, { signal });
    }
  }

  /* -- Initialize items that start open ---------------------------------- */

  #initOpenItems() {
    for (const item of this.#items) {
      const content = item.querySelector('.void-accordion__content');
      const trigger = item.querySelector('.void-accordion__trigger');
      if (!content || !trigger) continue;

      if (item.classList.contains('is-open')) {
        content.style.maxHeight = `${content.scrollHeight}px`;
        trigger.setAttribute('aria-expanded', 'true');
        content.setAttribute('aria-hidden', 'false');
      } else {
        content.style.maxHeight = '0px';
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');
      }

      content.style.overflow = 'hidden';
      content.style.transition = 'max-height 0.35s ease';
    }
  }

  /* -- Toggle an item ---------------------------------------------------- */

  /**
   * @param {HTMLElement} item - The .void-accordion__item to toggle
   */
  #toggle(item) {
    const isOpen = item.classList.contains('is-open');

    if (this.#singleMode && !isOpen) {
      for (const other of this.#items) {
        if (other !== item && other.classList.contains('is-open')) {
          this.#close(other);
        }
      }
    }

    if (isOpen) {
      this.#close(item);
    } else {
      this.#open(item);
    }
  }

  /**
   * @param {HTMLElement} item
   */
  #open(item) {
    const content = item.querySelector('.void-accordion__content');
    const trigger = item.querySelector('.void-accordion__trigger');
    if (!content || !trigger) return;

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');

    content.style.maxHeight = `${content.scrollHeight}px`;

    const onEnd = () => {
      content.style.maxHeight = 'auto';
      content.removeEventListener('transitionend', onEnd);
    };
    content.addEventListener('transitionend', onEnd);
  }

  /**
   * @param {HTMLElement} item
   */
  #close(item) {
    const content = item.querySelector('.void-accordion__content');
    const trigger = item.querySelector('.void-accordion__trigger');
    if (!content || !trigger) return;

    content.style.maxHeight = `${content.scrollHeight}px`;

    // Force reflow so the browser registers the concrete value
    void content.offsetHeight;

    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');

    content.style.maxHeight = '0px';
  }

  /* -- Cleanup ----------------------------------------------------------- */

  destroy() {
    this.#controller.abort();
    this.#container.removeAttribute('data-void-accordion-init');
  }
}

/* --------------------------------------------------------------------------
   Auto-initialization
   -------------------------------------------------------------------------- */

/** @type {VoidAccordion[]} */
const accordionInstances = [];

/**
 * Initialize all accordions found within a root element.
 * Skips containers already initialized (data-void-accordion-init).
 * @param {HTMLElement|Document} root
 */
const initAccordions = (root = document) => {
  root.querySelectorAll('.void-accordion:not([data-void-accordion-init])').forEach((el) => {
    accordionInstances.push(new VoidAccordion(/** @type {HTMLElement} */ (el)));
  });
};

document.addEventListener('DOMContentLoaded', () => initAccordions());

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) initAccordions(/** @type {HTMLElement} */ (event.target));
});
